import { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  getListMakeupRequestsQueryKey,
  listMakeupRequests,
} from '../../api/generated/scheduling/scheduling';
import type { MakeupRequest } from '../../api/generated/model';

/** Teto por pagina pedido ao servidor; o contrato aceita ate 200. */
const MAKEUP_PAGE_LIMIT = 100;

/**
 * A FILA INTEIRA de pedidos de reposicao, e nao a primeira pagina dela.
 *
 * As tres consultas de reposicao nao tinham LIMIT no servidor, e por isso `find`
 * sobre a lista sempre encontrava. Quando a paginacao chegou —
 * `LIMIT sqlc.arg('page_size')` com padrao de 50 —, as duas telas continuaram
 * pedindo sem cursor e passaram a enxergar so as 50 mais recentes. O estrago nao
 * e "ver menos": um professor com 50 pedidos mais novos que o alvo abre
 * `/makeup/<id>/respond` e recebe "Nao foi possivel carregar", e nao ha caminho
 * no produto para aceitar nem para recusar aquele pedido. Como
 * `ExpireMakeupWindows` fecha a janela por decurso de prazo, o pedido preso vira
 * reposicao perdida para o aluno — e o professor nao tem como evitar.
 *
 * Seguir o cursor ate `nextCursor` nulo, e nao um botao de "carregar mais": a
 * tela de resposta procura UM id, e um id ausente e indistinguivel de um id que
 * ainda nao chegou. Enquanto houver pagina por buscar, `items` fica `undefined`
 * — lista parcial aqui nao e "menos dados", e sim a tela dizendo que o pedido nao
 * existe.
 *
 * Nao ha leitura unitaria de pedido no contrato (`getMakeupRequest` nao existe),
 * e por isso a lista e a unica fonte. Recusada a alternativa de filtrar por
 * `state=requested` para reduzir a chance: ela nao fecha o caso — encolhe a
 * populacao e deixa o mesmo buraco — e ainda esconderia o pedido ja respondido,
 * que e o que explica o 409 na propria tela.
 *
 * A chave e a mesma sem parametros de `getListMakeupRequestsQueryKey()`, entao as
 * duas telas compartilham UMA consulta: o `limit` dentro da chave deixaria cada
 * tela com um cache proprio da mesma fila, e qualquer invalidacao mirando a
 * consulta sem parametros nao acertaria nenhuma das duas.
 */
export function useAllMakeupRequests(): {
  readonly items: readonly MakeupRequest[] | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
} {
  const query = useInfiniteQuery({
    queryKey: getListMakeupRequestsQueryKey(),
    queryFn: ({ pageParam, signal }) =>
      listMakeupRequests(
        pageParam === undefined
          ? { limit: MAKEUP_PAGE_LIMIT }
          : { limit: MAKEUP_PAGE_LIMIT, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const { data, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // sem ele, a pagina que falha no meio da fila deixa `hasNextPage` verdadeiro
    // e devolve `isFetchingNextPage` a falso — a dependencia muda, o efeito
    // dispara de novo, falha de novo, e a aba martela em laco fechado a rota que
    // acabou de recusar, que e o pior momento possivel para insistir.
    // `app/queryClient.ts` define `retry: false`, entao nada amortece a
    // repeticao: cada tentativa fracassa e reagenda a seguinte no mesmo ciclo,
    // enquanto o navegador estiver aberto.
    //
    // Este era o unico dos sete consumidores de `fetchNextPage` da SPA sem a
    // terceira condicao; os outros seis a trazem palavra por palavra.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const complete = data !== undefined && !hasNextPage;

  return {
    items: complete ? data.pages.flatMap((page) => page.items) : undefined,
    // Seguir o cursor ainda e carregar. Sem isto a tela de resposta declararia
    // "pedido nao encontrado" no intervalo entre a primeira pagina e a segunda.
    //
    // `query.isError` ja cobre a pagina que falha NO MEIO da varredura, e nao so
    // a primeira: `isFetchNextPageError` e DERIVADO de `isError`
    // (`query-core/infiniteQueryObserver.js`: `isError && fetchDirection ===
    // 'forward'`), entao ele implica `isError` em vez de substitui-lo — a falha
    // de uma pagina seguinte poe a consulta inteira em erro, com os dados ja
    // recebidos preservados. Recusado somar os dois aqui: `isError ||
    // isFetchNextPageError` se leria como a existencia de um caso que a
    // biblioteca nao produz, e comentario que descreve guarda inexistente e o
    // que faz o proximo leitor parar de procurar.
    isPending: !complete && !query.isError,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
